'use client';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import React from 'react';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { TelegramTransactionsPaymentsSystemsResponseDto } from '@/generated';
import clsx from 'clsx';
import { CurrencySign } from '../CurrencySign';

type Props = {
  onClose: () => void;
  fiatAmountToPay: number;
  coinsAmountToPay?: number;
  telegramStarsAmount?: number;
  showCoinsAlternative?: boolean;
  availableMethods: TelegramTransactionsPaymentsSystemsResponseDto[];
  onBack: () => void;
  onSelect: (id: string) => void;
  balance?: number;
  currencySign?: string;
};

const defaultIconLink = '/assets/images/payment-amount-default.png';

export const ChooseMethod: React.FC<Props> = ({
  fiatAmountToPay,
  coinsAmountToPay,
  telegramStarsAmount,
  showCoinsAlternative = false,
  availableMethods,
  onClose,
  onSelect,
  onBack,
  balance,
  currencySign = '₪',
}) => {
  const t = useTranslations('monetization');

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <Button variant='ghost' onClick={onBack} className='self-start size-[32px] p-0 rounded-full'>
          <IconChevronLeft className='text-text-primary' />
        </Button>
        <div className='flex flex-col'>
          <DialogHeader className='items-center text-center'>
            <DialogTitle>{t('choose_method')}</DialogTitle>
          </DialogHeader>

          <div className='rounded-[12px] bg-bg2-day py-[12px] px-[8px] flex flex-col gap-[8px] mt-[24px]'>
            <span className='text-[16px] text-text-disabled-day leading-[20px]'>{t('to_pay')}</span>

            <div className='ms-[8px] text-[22px] font-semibold'>
              {fiatAmountToPay} <CurrencySign value={currencySign} />
              {telegramStarsAmount !== undefined && (
                <span>
                  {' '}
                  ({telegramStarsAmount} <CurrencySign value='⭐' />)
                </span>
              )}
            </div>

            <div
              className={clsx('text-[16px] text-text-disabled-day font-normal', {
                hidden: !showCoinsAlternative || coinsAmountToPay === undefined,
              })}
            >
              {t('to_pay_coins')}{' '}
              <span className='text-primary'>{t('to_pay_coins_amount', { value: coinsAmountToPay ?? 0 })}</span>
            </div>
          </div>

          <div className=' border-b border-solid border-primary-stroke-day text-text-disabled-day text-[16px] mt-[48px]'>
            {t('choose_method')}:
          </div>
          <div className='flex flex-col mt-[16px]'>
            {availableMethods.map(({ paymentSystem, name, iconUrl }) => (
              <div
                key={paymentSystem}
                onClick={() => {
                  onSelect(paymentSystem);
                }}
                className='flex justify-between gap-[8px] py-[12px] items-center cursor-pointer'
              >
                <div className='flex gap-[8px] items-center'>
                  <Image
                    width={60}
                    height={28}
                    alt=''
                    src={iconUrl ? iconUrl : defaultIconLink}
                    className={clsx('w-[60px] h-[28px]', { 'w-[28px]!': paymentSystem === 'COINS' })}
                  />

                  <div className='flex flex-col'>
                    <div className='text-[16px] leading-[18px]'>{name}</div>
                    {paymentSystem === 'COINS' && balance !== undefined && (
                      <div className='text-text-disabled-day text-[14px]'>
                        {t('balance_amount', { value: balance })}
                      </div>
                    )}
                  </div>
                </div>
                <IconChevronRight className='justify-self-end' />
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
